import React from 'react'
import { AccordionItem, AccordionItemContent } from '@e3dc-react/shell/Elements/Accordion'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Box, Typography } from '@mui/material'
import { FormSectionTitle } from 'src/Libs/flow/components'
import useAccordionStyle from 'src/Libs/styles'
import { NumberField, Select, TabButtonGroup } from 'src/Libs/inputs/react-hook-form'
import { SettingsBox } from 'src/Elements/Data'
import { FormCheckbox } from 'src/Libs/inputs/react-hook-form/Checkbox'
import { CURRENT_TRANSFORMER_RATIOS } from '../constants'
import { InstallationFormModel } from 'src/Libs/flow/installation/forms'
import { TFunction } from 'i18next'
import { useElectricalGridSettings } from '../hooks'
import { UnitTypes } from 'src/Libs/types/common'
import { useInstallationAccessRightsContext } from 'src/Libs/installation-rights-management/provider/InstallationAccessRightsContext'
import { useUser } from 'src/Hooks/useUser'
import { useSectionFormStatus } from '../../hooks'

const PHASE_OPTIONS = (t: TFunction): Options<InstallationFormModel['generalSettings']['gridSettings']['numberOfPhases']> => [
  { value: 'single-phase', label: t('installation.electricalGrid.onePhase') },
  { value: 'three-phases', label: t('installation.electricalGrid.threePhases') },
]

export const ElectricalGridSettings: React.FC = () => {
  const { t } = useCustomTranslation()
  const { classes } = useAccordionStyle()
  const { hasInstallationWritingRights: canEdit } = useInstallationAccessRightsContext()
  const { handlePhaseChange, handleCheckboxChange, handleMainProtectionChange, hasEmc, hasElectricalCurrentTransformer } = useElectricalGridSettings()
  const { userData } = useUser()
  const { isDirty, isError } = useSectionFormStatus(
    dirtyFields => [dirtyFields.generalSettings?.gridSettings],
    errors => [errors.generalSettings?.gridSettings]
  )

  const isUserLevelUser = userData?.level === 'USER'
  const isFieldDisabled = isUserLevelUser || !canEdit

  return (
    <AccordionItem
      title={<FormSectionTitle name={t('installation.configOverview.electricalGrid')} isDirty={isDirty} isError={isError} />}
      expanded
      className={classes.accordion}
      sx={{ margin: 0 }}
    >
      <AccordionItemContent variant="paper">
        <SettingsBox
          title={t('installation.electricalGrid.phaseType')}
          suffix={
            <Box justifyContent={'flex-start'} sx={{ minWidth: { xs: '100%', sm: '50%' } }}>
              <TabButtonGroup
                name="generalSettings.gridSettings.numberOfPhases"
                options={PHASE_OPTIONS(t)}
                onChange={handlePhaseChange}
                disabled={isFieldDisabled}
              />
            </Box>
          }
        />
        <SettingsBox
          title={t('installation.electricalGrid.mainProtection')}
          suffix={
            <Box sx={{ minWidth: { xs: '100%', sm: 'auto' } }}>
              <NumberField
                name="generalSettings.gridSettings.mainProtection"
                label={t('installation.electricalGrid.mainProtection')}
                suffix={UnitTypes.ampere}
                propsOnChange={handleMainProtectionChange}
                fullWidth
                disabled={isFieldDisabled}
              />
            </Box>
          }
        />
        <SettingsBox
          title={t('installation.electricalGrid.derated')}
          suffix={
            <Box sx={{ minWidth: { xs: '100%', sm: 'auto' } }}>
              <NumberField
                name="generalSettings.gridSettings.thermalDerating"
                label={t('installation.electricalGrid.derated')}
                suffix={UnitTypes.ampere}
                fullWidth
                disabled={isFieldDisabled}
              />
            </Box>
          }
        />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 2 }}>
          <Typography component="h3" fontWeight="bold">
            {t('installation.electricalGrid.gridMeter')}
          </Typography>
          <SettingsBox
            title={t('installation.electricalGrid.ratio')}
            suffix={
              <Box
                sx={{
                  display: 'flex',
                  width: '100%',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 2,
                  alignItems: 'flex-start',
                  justifyContent: 'end',
                }}
              >
                <FormCheckbox
                  name="generalSettings.gridSettings.gridMeter.hasElectricalCurrentTransformer"
                  label={t('installation.electricalGrid.currentTransformer')}
                  readOnly={!hasEmc}
                  disabled={isFieldDisabled}
                  onChange={handleCheckboxChange}
                />

                {hasElectricalCurrentTransformer ? (
                  <Select
                    label={t('installation.electricalGrid.ratio')}
                    name="generalSettings.gridSettings.gridMeter.electricalCurrentTransformerRatio"
                    options={CURRENT_TRANSFORMER_RATIOS}
                    disabled={isFieldDisabled}
                  />
                ) : (
                  <DisabledSelect />
                )}
              </Box>
            }
          />
        </Box>
      </AccordionItemContent>
    </AccordionItem>
  )
}

const DisabledSelect: React.FC = () => {
  const { t } = useCustomTranslation()
  return (
    <Select
      disabled
      name="generalSettings.gridSettings.gridMeter.electricalCurrentTransformerRatio"
      label={t('installation.electricalGrid.ratio')}
      value={'1:1'}
      options={[{ value: '1:1', label: '1:1' }]}
    />
  )
}
